/**
 * Auto-collection of offline-replay mutation definitions.
 *
 * A replayable mutation opts in by declaring `offlineReplay: true` on its
 * exported def object (see `hooks/<domain>/use*.ts`). Previously each such
 * `*Mutations` export ALSO had to be hand-added to a hard-coded array in
 * `main.tsx`; forgetting that step meant the mutation silently never replayed
 * after a reload / BackgroundSync — a correctness gap behind a convention
 * (issue #116).
 *
 * Instead, this module eagerly imports every `hooks/**` module via Vite's
 * `import.meta.glob` and scans their exports for objects carrying the
 * replayable-def shape. Declaring `offlineReplay: true` on a def under
 * `hooks/` is now sufficient — no per-mutation edit here or in `main.tsx`.
 *
 * NOTE: this does NOT register anything itself — it only discovers the defs.
 * `main.tsx` owns the `setMutationDefaults` wiring (mutationFn / scope /
 * onSettled) and the `shouldDehydrateMutation` persistence gate, so the replay,
 * idempotency, and dehydration semantics live in exactly one place.
 */
import { hashKey } from '@tanstack/react-query'

/** The shape `main.tsx` needs to register a mutation for offline replay. */
type ReplayableMutationDef = {
  mutationKey: readonly unknown[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mutationFn: (...args: any[]) => Promise<unknown>
  offlineReplay?: boolean
  scope?: { id: string }
  settledInvalidation?: readonly (readonly unknown[])[]
}

/** Structural check: is `value` an individual replayable mutation def? */
function isReplayableDef(value: unknown): value is ReplayableMutationDef {
  if (typeof value !== 'object' || value === null) return false
  const def = value as Record<string, unknown>
  return def.offlineReplay === true && Array.isArray(def.mutationKey) && typeof def.mutationFn === 'function'
}

/**
 * Discover every replayable mutation def across the hooks layer.
 *
 * Eagerly loads all `hooks/**` modules (they're pure at module scope — the
 * factories just build closures, and the modules carry no heavy transitive
 * deps, so this is not a meaningful bundle cost) and walks each export. An
 * export is either an individual def or, more commonly, a `*Mutations` record
 * whose values are defs; both forms are flattened. Only defs with
 * `offlineReplay: true` are returned, deduplicated by mutation-key hash so a
 * def re-exported from two places can't register twice.
 *
 * The glob is scoped to `hooks/` — replayable mutation defs live in the data
 * layer by convention (documented in `hooks/CLAUDE.md`). Scoping here (rather
 * than all of `src/`) keeps `pages/`/`components/` route chunks out of the
 * entry bundle. The dev-only floor check below guards against a def silently
 * falling out of range (e.g. a broken glob or a misplaced def).
 */
let cachedDefs: ReplayableMutationDef[] | undefined

export function collectReplayableMutations(): ReplayableMutationDef[] {
  // Memoized: the replayable def set is fixed at build time. Both `main.tsx`
  // (directly) and `replayableMutationKeyHashes()` call this at startup, so
  // caching keeps the eager glob scan to a single pass.
  if (cachedDefs) return cachedDefs

  // Eager glob: every hook module is imported at startup so its def exports are
  // present before registration. Path is root-relative to the Vite project.
  // Test files are explicitly excluded — this is a static glob Vite resolves
  // from the filesystem at build time (not runtime-scoped to test), so an
  // unfiltered pattern would eagerly bundle `*.test.ts` (and its `vitest`/
  // `@testing-library` imports) straight into the production app.
  const modules = import.meta.glob(['/src/hooks/**/*.{ts,tsx}', '!/src/hooks/**/*.test.{ts,tsx}'], {
    eager: true,
  }) as Record<string, Record<string, unknown>>

  const defs: ReplayableMutationDef[] = []
  const seenKeys = new Set<string>()

  const consider = (value: unknown) => {
    if (!isReplayableDef(value)) return
    // Dedup with the SAME hash main.tsx uses for `replayableKeyHashes` /
    // `shouldDehydrateMutation`, so the collector and the persistence gate
    // can never disagree about a key (hashKey sorts plain-object props;
    // JSON.stringify would not).
    const keyHash = hashKey(value.mutationKey)
    if (seenKeys.has(keyHash)) return
    seenKeys.add(keyHash)
    defs.push(value)
  }

  for (const mod of Object.values(modules)) {
    for (const exported of Object.values(mod)) {
      // Direct def export, or a `*Mutations` record of defs.
      consider(exported)
      if (exported && typeof exported === 'object') {
        for (const nested of Object.values(exported as Record<string, unknown>)) {
          consider(nested)
        }
      }
    }
  }

  // Dev-only safety net: if discovery ever finds nothing, the glob or the
  // def convention has broken — surface it loudly rather than silently
  // shipping an app where no offline write replays.
  if (import.meta.env.DEV && defs.length === 0) {
    console.warn(
      '[offlineMutations] No replayable mutation defs discovered. Offline writes will not replay — check the import.meta.glob path and that defs under src/hooks declare `offlineReplay: true`.',
    )
  }

  cachedDefs = defs
  return defs
}

let cachedKeyHashes: Set<string> | undefined

/**
 * The set of mutation-key hashes that belong to the offline-replay queue —
 * the SAME hash (`hashKey`) `main.tsx` uses for `shouldDehydrateMutation`, so
 * every consumer agrees on "is this mutation replayable" from one source.
 *
 * Two consumers rely on this:
 * - `main.tsx` — the persistence gate (only replayable paused mutations are
 *   dehydrated).
 * - `useFailedMutations` — the FailedMutationsSheet is the replay-QUEUE surface,
 *   so it must show ONLY these keys, never a foreground form submission (deck
 *   create/import) that errored, which would otherwise leak in as an
 *   "Unknown change" with a Retry that can never succeed (#204).
 *
 * Memoized: the replayable def set is fixed at build time.
 */
export function replayableMutationKeyHashes(): Set<string> {
  cachedKeyHashes ??= new Set(collectReplayableMutations().map((def) => hashKey(def.mutationKey)))
  return cachedKeyHashes
}
